import './index.scss';

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// fix: https://github.com/react-syntax-highlighter/react-syntax-highlighter/pull/521
import SyntaxHighlighter from '@fengkx/react-syntax-highlighter';
import { atomOneDark } from '@fengkx/react-syntax-highlighter/dist/esm/styles/hljs';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import rehypeRaw from 'rehype-raw';
import copy from 'copy-to-clipboard';
import Other from '@/common/helper/other';
import { escapeLaTeX } from '@shared/utils';
import cacheStore from '@/store/cache';
import { HelperCore } from '@/common/dependencies';
import McpToolsAudio from '../chat-history/mcpItem/tools-audio';
import McpToolsVideo from '../chat-history/mcpItem/tools-video';

const LANGUAGE_RE = /language-(\w+)/;

const Markdown = props => {
  const { className, components, typing, highlighterConfig = { style : atomOneDark }, text = '', codeInfo } = props;

  if(typeof text != 'string' || !text || !text.trim()){
    return null;
  }

  const markdown = useMemo(
    () => (
      <ReactMarkdown
        className={['markdown-container', className, { typing }]}
        children={escapeLaTeX(text)}
        components={{
          iframe() {
            return null;
          },
          script() {
            return null;
          },
          object() {
            return null;
          },
          form(){
            return null;
          },
          code({ node, inline, className, children, ...props }) {
            const match = LANGUAGE_RE.exec(className);
            if (match) {
              if (match[1] === 'math') {
                return (
                  <code {...props} className={className}>
                    {children}
                  </code>
                );
              }

              if(match[1] === 'html' && codeInfo?.title){
                return null;
              }

              return (
                <div className="!rounded-[8px] overflow-hidden mt-[10px]">
                  <div
                    key="header"
                    className="code-language-container flex justify-between items-center h-[40px] bg-[#1C202A] text-[rgba(255,255,255,.6)] px-[16px] text-[14px] js-article-copy-exclude"
                  >
                    <div className="first-letter:uppercase">
                      {match[1] || ''}
                    </div>
                    <div
                      className={[
                        'flex items-center',
                        typing ? '' : 'cursor-pointer',
                      ]}
                      onClick={() => {
                        if (typing) {
                          return;
                        }

                        const copied = copy(
                          String(children).replace(/\n$/, '')
                        );
                        copied && Other.toast('复制成功');
                      }}
                    >
                      <div className="ml-[4px]">复制</div>
                    </div>
                  </div>
                  <SyntaxHighlighter
                    {...props}
                    wrapLongLines
                    className={`highlighter-box !p-[15px] text-[14px] leading-relaxed ${highlighterConfig?.className ?? ''}`}
                    customStyle={{
                      fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', 
                    }}
                    children={String(children).replace(/\n$/, '')}
                    {...highlighterConfig}
                    language={match[1]}
                    PreTag="div"
                  />
                </div>
              );
            }

            return (
              <code
                {...props}
                className={[
                  'whitespace-pre-wrap text-[13px] bg-[#1b1f230d]',
                  'rounded-[3px] p-[3px]',
                  className,
                ]}
              >
                {children}
              </code>
            );
          },
          table({ node, className, children, ...props }) {
            return (
              <div
                className="markdown__table-container rounded-7px overflow-hidden overflow-x-scroll p-0 my-16px"
                style={{
                  border: "1px solid #D6D6D6",
                }}>
                <table {...props} className={[className]}>
                  {children}
                </table>
              </div>
            );
          },
          a({ href, children, ...props }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            );
          },
          img({ node, className, children, src, alt, ...props }){
            if(!src || cacheStore.isErrorResource(src)){
              return null;
            }

            // MCP 总结中的音视频
            const url = HelperCore.getUrlWithOutParams(src);
            if(/\.mp3/i.test(url)){
              return <McpToolsAudio event={{ url : src }} />
            }
            if(/\.(mp4)/i.test(url)){
              return <McpToolsVideo event={{ url : src }} />
            }

            return (
              <img 
                className={`max-w-full ${className}`} 
                src={src} 
                alt={alt} 
                onError={
                  (event) => {
                    cacheStore.addErrorResource(src);   
                    // the node to be removed is not a child of this node
                    try{
                      $(event.target).hide();
                    } catch(e){}
                  }
                }
                />
            )
          },
          ...components,
        }}
        remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkMath]}
        rehypePlugins={[...(/\[\^(\d)+\]/.test(text) ? [] : [rehypeKatex]), rehypeRaw]}
      />
    ),
    [text]
  );

  return markdown;
};

export default Markdown;
